import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import AuthLayout from '@/components/AuthLayout';
import api from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, CheckCircle } from 'lucide-react';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setSent(true);
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout heading="Forgot password" subtitle="We'll send a reset link to your email address">
      <div className="p-8" style={{ backgroundColor: '#FFFFFF', border: '1px solid #000' }}>
        {sent ? (
          <div className="text-center space-y-4 py-4">
            <CheckCircle size={40} strokeWidth={1.5} className="mx-auto" />
            <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>Check your inbox</p>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              If <strong>{email}</strong> is registered, you will receive a password reset link within a few minutes.
            </p>
            <Link
              to="/login"
              className="text-xs font-medium mt-2 inline-block"
              style={{ color: '#000', textDecoration: 'underline', textUnderlineOffset: '3px' }}
            >
              Back to login
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>
                Email address
              </Label>
              <Input
                id="email"
                type="email"
                required
                placeholder="you@example.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="h-11 rounded-none"
                style={{ borderColor: 'var(--border-clr)', backgroundColor: '#FDFCFA' }}
                data-testid="forgot-password-email"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-12 flex items-center justify-center gap-2 mt-2 uppercase tracking-wider font-semibold"
              disabled={loading}
              data-testid="forgot-password-submit"
              style={{ backgroundColor: '#000', color: 'white', borderRadius: 0, fontSize: '0.8rem' }}
              onMouseEnter={e => { if (!loading) { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.color = '#000'; e.currentTarget.style.boxShadow = 'inset 0 0 0 1px #000'; } }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#000'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                    <path d="M12 2a10 10 0 0 1 10 10" />
                  </svg>
                  Sending…
                </span>
              ) : (<><span>Send reset link</span><ArrowRight size={15} /></>)}
            </Button>
          </form>
        )}
      </div>

      {!sent && (
        <div className="mt-6 text-center">
          <Link to="/login" className="text-sm flex items-center justify-center gap-1.5" style={{ color: 'var(--text-muted)' }}>
            <ArrowLeft size={14} /> Back to login
          </Link>
        </div>
      )}
    </AuthLayout>
  );
}
